'use client'

import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { ChevronDown, ChevronUp, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { useSearchParams } from 'next/navigation'

import 'leaflet/dist/leaflet.css'

import dynamic from 'next/dynamic'
import { RawStoreData } from '@/lib/stores/store-types'
import StoreFilters, { FilterState } from '@/components/StoreFilters'
import StoreListItem from '@/components/StoreListItem'
import { Locale, getStartingLocationByLocale } from '@/lib/i18n'
import { getCurrentTimeAtLocation } from '@/lib/client-get-time-at-lat-lng'
import { fetchNearbyStores, SearchLocation } from '@/lib/utils.client'
import NavigationBar from '../NavigationBar'
/**
 * Lazy-loaded Leaflet map component.
 * SSR is disabled because Leaflet requires the `window` object.
 */
const LazyMap = dynamic(() => import('@/components/StoreMap'), {
	ssr: false,
	loading: () => <p>Loading...</p>,
})

/** SessionStorage key used to persist the user's selected search radius across page navigations. */
const RADIUS_STORAGE_KEY = 'ua-store-locator-radius'

/**
 * Main store locator page component.
 *
 * This component renders a two-panel layout:
 * - **Left panel**: Search form, store type filters, and a scrollable list of matching stores.
 * - **Right panel**: A Leaflet map showing store markers.
 *
 * ## Props
 * - `dictionary` — Localized UI strings keyed by translation key (e.g. `'find-a-store-near-you'`).
 * - `locale` — The active locale in `lang-country` format (e.g. `'en-us'`, `'de-de'`). Used to
 *   determine the default search location, unit system (miles vs km), and geocoder locale bias.
 *
 * ## Data flow
 *
 * ```
 * ┌─ Page loads ─────────────────────────────────────────────────────────┐
 * │  useEffect (init): URL has ?q= → searchForStores(query)             │
 * │                    no ?q=      → fetchStoresForLocation(default)     │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ User searches ──────────────────────────────────────────────────────┐
 * │  form submit → searchForStores(address)                             │
 * │    1. Geocode address → { lat, lng, country }                       │
 * │    2. Fetch /api/stores/nearby with country filter                  │
 * │    3. Set searchLocation + matchingStores in one shot               │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ User changes radius ────────────────────────────────────────────────┐
 * │  handleSearchRangeChange: re-fetch stores for current location       │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ Store type filtering ───────────────────────────────────────────────┐
 * │  useMemo: filteredStores = matchingStores filtered by checkboxes    │
 * │  (derived data — no extra render cycle)                             │
 * └──────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * ## Country filtering
 * The country filter is based on the **geocoded place**, not the user's locale.
 * This enables cross-country searches (e.g. searching "London" from the `en-us` locale
 * correctly returns GB stores). The geocoder extracts the country from the API response
 * (Google, Mapbox, or Nominatim) and passes it to the nearby stores API.
 *
 * ## useEffect summary
 * | Effect | Deps | Purpose |
 * |--------|------|---------|
 * | Init | `[searchParams, locale]` | Load initial stores on mount / URL change |
 * | Iframe resize | `[postIframeDimensions]` | Communicate height to parent iframe |
 */
export default function StoreLocator({ dictionary, locale }: { dictionary: Record<string, string>; locale: Locale }) {
	const searchParams = useSearchParams()

	/** Ref to the address text input — used to read/write the input value imperatively. */
	const addressField = useRef<HTMLInputElement>(null)

	/** Loading indicator text shown in the store list area (e.g. "Finding stores..."). Null = not loading. */
	const [loadingText, setLoadingText] = useState<string | null>(null)

	/** The current time at the searched location — used to determine if stores are open or closed. */
	const [currentTime, setCurrentTime] = useState<Date | null>(null)

	/** The store currently highlighted in the list (clicked by user). Passed to the map for marker emphasis. */
	const [storeToHighlight, setStoreToHighlight] = useState<RawStoreData | null>(null)

	/** Whether the store type filters collapsible section is expanded. */
	const [filtersExpanded, setFiltersExpanded] = useState(false)

	/** Store type filter checkboxes state. All enabled by default so every store type is shown. */
	const [filters, setFilters] = useState<FilterState>({
		brandHouses: true,
		factoryHouses: true,
		dealers: true,
		popUp: true,
	})

	/** All stores returned by the nearby stores API for the current search location + radius. */
	const [matchingStores, setMatchingStores] = useState<RawStoreData[]>([])

	/** Locales that display distances in miles. All others use kilometers. */
	const milesLocales = ['en-us']
	const usesMiles = milesLocales.includes(locale)

	/**
	 * Reads the initial search radius from sessionStorage (if previously set),
	 * otherwise defaults to 50 miles (US) or 25 km (everywhere else).
	 */
	const getInitialSearchRange = () => {
		if (typeof window !== 'undefined') {
			const stored = sessionStorage.getItem(RADIUS_STORAGE_KEY)
			if (stored) return Number(stored)
		}
		return usesMiles ? 50 : 25
	}

	/** The user's currently selected search radius (in miles or km depending on locale). */
	const [searchRange, setSearchRange] = useState(getInitialSearchRange)

	/** Tracks whether the search input is empty — used to disable the Search button. */
	const [isSearchEmpty, setIsSearchEmpty] = useState(true)

	/** Dropdown options for the search radius selector — varies by unit system. */
	const rangeOptions = usesMiles
		? [
				{ value: 15, label: '15 miles' },
				{ value: 30, label: '30 miles' },
				{ value: 50, label: '50 miles' },
			]
		: [
				{ value: 25, label: '25 km' },
				{ value: 50, label: '50 km' },
				{ value: 80, label: '80 km' },
			]

	/**
	 * The resolved geocoding result for the current map view: latitude, longitude, and country.
	 * Set after geocoding a search query or using the geolocation button.
	 */
	const [searchLocation, setSearchLocation] = useState<SearchLocation | null>(null)

	/**
	 * Highlights a store on the map when clicked in the list.
	 * Passed down to each `StoreListItem` as a callback.
	 */
	const highlightStore = useCallback((store: RawStoreData) => {
		setStoreToHighlight(store)
	}, [])

	/** Converts the UI search range to the API radius (always in miles). */
	const getRadiusInMiles = useCallback(
		(range: number) => (usesMiles ? range : Math.round(range * 0.621371)),
		[usesMiles],
	)

	/** Handles search radius dropdown changes: updates state, persists to sessionStorage, and re-fetches stores. */
	const handleSearchRangeChange = useCallback(
		(value: string) => {
			const range = Number(value)
			setSearchRange(range)
			if (typeof window !== 'undefined') {
				sessionStorage.setItem(RADIUS_STORAGE_KEY, String(range))
			}
			if (!searchLocation) return
			const radius = getRadiusInMiles(range)
			setLoadingText(dictionary['finding-stores'])
			fetchNearbyStores(searchLocation, radius).then((stores) => {
				setMatchingStores(stores)
				setLoadingText(null)
			})
		},
		[searchLocation, getRadiusInMiles, dictionary],
	)

	/**
	 * Core search function: geocodes an address, fetches nearby stores, and updates all state.
	 * Called on form submit and on initial load with a `?q=` param.
	 */
	const searchForStores = useCallback(
		async (address: string) => {
			setLoadingText(dictionary['calculating-location'])

			const geoResponse = await fetch(`/api/geocode/?address=${encodeURIComponent(address)}&locale=${locale}`)
			const geoData = await geoResponse.json()

			const location: SearchLocation = {
				latitude: geoData.latitude,
				longitude: geoData.longitude,
				country: geoData.country,
			}

			setLoadingText(dictionary['finding-stores'])

			const radius = getRadiusInMiles(searchRange)
			const stores = await fetchNearbyStores(location, radius)

			setSearchLocation(location)
			setMatchingStores(stores)
			getCurrentTimeAtLocation(location).then(setCurrentTime)
			setLoadingText(null)
		},
		[dictionary, locale, searchRange, getRadiusInMiles],
	)

	/**
	 * Fetches stores directly for a known location (no geocoding needed).
	 * Used for: default locale location on initial load and geolocation button.
	 */
	const fetchStoresForLocation = useCallback(
		async (location: SearchLocation) => {
			setLoadingText(dictionary['finding-stores'])

			const radius = getRadiusInMiles(searchRange)
			const stores = await fetchNearbyStores(location, radius)

			setSearchLocation(location)
			setMatchingStores(stores)
			getCurrentTimeAtLocation(location).then(setCurrentTime)
			setLoadingText(null)
		},
		[dictionary, searchRange, getRadiusInMiles],
	)

	/** Filters `matchingStores` by the active store type checkboxes (Brand House, Factory House, Dealer, PopUp). */
	const filteredStores = useMemo(
		() =>
			matchingStores.filter((store) => {
				if (filters.brandHouses && store.store_type === 'Brand House') return true
				if (filters.factoryHouses && store.store_type === 'Factory House') return true
				if (filters.dealers && store.store_type === 'Dealer') return true
				if (filters.popUp && store.store_type === 'PopUp') return true
				return false
			}),
		[matchingStores, filters],
	)

	/**
	 * Updates the `?q=` URL search parameter without triggering a full page reload.
	 * This keeps the URL shareable — if a user copies the URL, the recipient sees the same search.
	 * Uses `window.history.pushState` intentionally (instead of Next.js router) to avoid re-rendering.
	 */
	const updateSearchQuery = useCallback(
		(query: string) => {
			const params = new URLSearchParams(searchParams.toString())
			if (query) {
				params.set('q', query)
			} else {
				params.delete('q')
			}

			window.history.pushState(null, '', `?${params.toString()}`)
		},
		[searchParams],
	)

	/**
	 * Initialization effect: runs once on mount.
	 * - If URL has `?q=Berlin`, geocode and search for stores there.
	 * - Otherwise, load stores around the default location for the locale.
	 */
	useEffect(() => {
		const query = searchParams.get('q')
		if (query) {
			if (addressField.current) {
				addressField.current.value = query
			}
			searchForStores(query)
			setIsSearchEmpty(false)
		} else {
			const defaultLocation = getStartingLocationByLocale(locale)
			if (addressField.current) {
				addressField.current.value = defaultLocation.value
			}
			fetchStoresForLocation({
				latitude: defaultLocation.latitude,
				longitude: defaultLocation.longitude,
				country: locale.split('-')[1]?.toUpperCase(),
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount / URL change, not on every searchRange change
	}, [searchParams, locale])

	/** Form submit handler: geocode the typed address and fetch stores. */
	const onSearchSubmitted = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault()
			const searchQuery = addressField.current?.value || ''
			setStoreToHighlight(null)
			updateSearchQuery(searchQuery)
			searchForStores(searchQuery)
		},
		[searchForStores, updateSearchQuery],
	)

	/** Geolocation button: uses the browser's location API (no geocoding, no country filter). */
	const handleGetCurrentLocation = useCallback(() => {
		if (!navigator.geolocation) {
			alert(dictionary['geolocation-not-supported'] || 'Geolocation is not supported by your browser')
			return
		}

		setLoadingText(dictionary['getting-location'] || 'Getting your location...')
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const location: SearchLocation = {
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					// No country — browser geolocation doesn't provide it, so no country filter is applied
				}

				if (addressField.current) {
					addressField.current.value = `${position.coords.latitude}, ${position.coords.longitude}`
				}

				fetchStoresForLocation(location)
			},
			(error) => {
				console.error('Error getting location:', error)
				setLoadingText(null)
				alert(dictionary['location-error'] || 'Unable to retrieve your location')
			},
		)
	}, [dictionary, fetchStoresForLocation])

	/** Ref to the outermost wrapper div — used to measure dimensions for iframe postMessage. */
	const wrapperRef = useRef<HTMLDivElement>(null)

	/**
	 * Posts the component's dimensions to the parent window via postMessage.
	 * This is used when the store locator is embedded as an iframe inside the main
	 * Seabiscuit site — the parent needs to know the iframe height to size it correctly.
	 * The 106px offset accounts for the Seabiscuit site header.
	 */
	const postIframeDimensions = useCallback(() => {
		if (wrapperRef.current) {
			const height = window.outerHeight - 106
			const width = window.outerHeight
			window.parent.postMessage(`w2gi:iframeHeightUpdated//${height}//${width}`, '*')
		}
	}, [])

	/**
	 * useEffect: Iframe dimension sync.
	 * Posts dimensions on mount and on every window resize, so the parent iframe
	 * container always has the correct height.
	 */
	useEffect(() => {
		postIframeDimensions()

		const handleResize = () => postIframeDimensions()
		window.addEventListener('resize', handleResize)

		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [postIframeDimensions])

	return (
		<div ref={wrapperRef} className="flex flex-col lg:flex-row w-full h-screen bg-white" role="main">
			{/* Left Panel */}
			<div className="w-full lg:w-[400px] flex flex-col border-r border-gray-200" role="complementary">
				<div className="flex flex-row gap-2 justify-between items-center border-b border-gray-200">
					<NavigationBar
						dictionary={dictionary}
						locale={locale}
						visibleItems={['back-button', 'store-index', 'return-policy']}
					/>
				</div>

				{/* Search Form */}
				<div className="p-4 border-b border-gray-200">
					<h1 className="text-xl font-bold mb-4">{dictionary['find-a-store-near-you']}</h1>
					<div className="space-y-3">
						<form onSubmit={onSearchSubmitted} className="flex flex-col gap-2" role="search">
							<label htmlFor="location-search" className="sr-only">
								{dictionary['enter-location']}
							</label>
							<div className="flex gap-2">
								<input
									id="location-search"
									type="text"
									placeholder={dictionary['enter-location']}
									className="w-full p-1 border border-gray-300"
									ref={addressField}
									aria-label={dictionary['enter-location']}
									aria-describedby="location-search-description"
									onChange={(e) => setIsSearchEmpty(!e.target.value.trim())}
								/>
								<Select value={String(searchRange)} onValueChange={handleSearchRangeChange}>
									<SelectTrigger className="h-full rounded-none border-gray-300">
										<SelectValue aria-label={dictionary['search-range'] || 'Range'} />
									</SelectTrigger>
									<SelectContent>
										{rangeOptions.map((opt) => (
											<SelectItem key={opt.value} value={String(opt.value)}>
												{opt.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div id="location-search-description" className="sr-only">
								{dictionary['enter-location']}
							</div>

							<div className="flex gap-2">
								<Button
									className="flex-1 bg-red-600 hover:bg-red-700 text-white py-1 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
									type="submit"
									aria-label={dictionary['search']}
									disabled={isSearchEmpty}
								>
									{dictionary['search']}
								</Button>
								<Button
									type="button"
									onClick={handleGetCurrentLocation}
									title={dictionary['get-current-location']}
									className="bg-gray-600 hover:bg-gray-700 text-white py-1 rounded-none"
									aria-label={dictionary['get-current-location']}
								>
									<MapPin size={20} aria-hidden="true" />
								</Button>
							</div>
						</form>
					</div>
				</div>

				{/* Filters */}
				<div className="border-b border-gray-200">
					<button
						className="w-full p-4 flex justify-between items-center font-bold"
						onClick={() => setFiltersExpanded(!filtersExpanded)}
						aria-expanded={filtersExpanded}
						aria-controls="filters-content"
					>
						{dictionary['filters']}
						{filtersExpanded ? (
							<ChevronUp size={20} aria-hidden="true" />
						) : (
							<ChevronDown size={20} aria-hidden="true" />
						)}
					</button>

					<div
						id="filters-content"
						className={`transition-all duration-300 ease-in-out overflow-hidden ${filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
						role="region"
						aria-label={dictionary['filters']}
					>
						<StoreFilters dictionary={dictionary} filters={filters} setFilters={setFilters} />
					</div>
				</div>

				{/* Store Details */}
				<div
					className="p-4 border-b border-gray-200 flex flex-col gap-2 overflow-auto"
					role="region"
					aria-label={dictionary['store-list']}
					aria-live="polite"
				>
					{!loadingText &&
						filteredStores.map((store) => (
							<StoreListItem
								key={store.id}
								locale={locale}
								store={store}
								dictionary={dictionary}
								highlightStore={highlightStore}
								currentTime={currentTime || new Date()}
							/>
						))}
					{loadingText && (
						<div className="text-center text-gray-500" role="status">
							{loadingText}
						</div>
					)}
					{!loadingText && filteredStores.length === 0 && (
						<div className="text-center text-gray-500" role="status">
							{dictionary['no-stores-found']}
						</div>
					)}
				</div>
			</div>

			{/* Map */}
			<div className="flex-1 relative invisible md:visible">
				<div className="absolute inset-0 bg-gray-200">
					<LazyMap
						stores={filteredStores}
						originalCenter={searchLocation || getStartingLocationByLocale(locale)}
						zoom={11}
						storeToHighlight={storeToHighlight}
						dictionary={dictionary}
						currentTime={currentTime || new Date()}
						locale={locale}
					/>
				</div>
			</div>
		</div>
	)
}
